// src/pages/EmployeeMyBookings.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import api from "../services/api";
import { toast } from "react-toastify";
import EmployeeLayout from "../components/EmployeeLayout";
import bookingWS from "../services/bookingWebSocket";
import locationWS from "../services/locationWebSocket";
import { getMainForkBookingDetails } from "../services/bookingService";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
  Polyline,
  useMap,
} from "react-leaflet";
import { DEFAULT_MAP_CENTER } from "../constants/mapDefaults";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { fetchBookings } from "../actions/BookingActions";
import { fetchUsers } from "../actions/UserActions";
import { fetchVehicles } from "../actions/VehicleActions";

const DEFAULT_CENTER = DEFAULT_MAP_CENTER;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const startIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [35, 35],
  iconAnchor: [17, 35],
  popupAnchor: [0, -35],
});

const endIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/149/149060.png",
  iconSize: [35, 35],
  iconAnchor: [17, 35],
  popupAnchor: [0, -35],
});

const fetchORSResults = async (query) => {
  const apiKey = process.env.REACT_APP_ORS_KEY;
  if (!apiKey || !query.trim()) return [];
  try {
    const url = `https://api.openrouteservice.org/geocode/search?api_key=${apiKey}&text=${encodeURIComponent(query)}&boundary.country=LK&size=5`;
    const res = await fetch(url);
    const data = await res.json();
    return data.features || [];
  } catch (err) {
    console.error("ORS search error:", err);
    return [];
  }
};

const fetchRoute = async (start, end) => {
  const apiKey = process.env.REACT_APP_ORS_KEY;
  if (!apiKey) return null;
  const url = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${start[1]},${start[0]}&end=${end[1]},${end[0]}`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    const feature = data.features?.[0];
    if (feature?.geometry?.coordinates && feature?.properties?.segments?.[0]) {
      const segment = feature.properties.segments[0];
      const geometry = feature.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
      return { geometry, distance: segment.distance, duration: segment.duration };
    }
  } catch (e) { console.error("ORS route error:", e); }
  return null;
};

function ModalRouteLayer({ start, end, onRouteUpdate }) {
  const [route, setRoute] = useState([]);
  const map = useMap();
  useEffect(() => {
    if (!start || !end) { setRoute([]); onRouteUpdate(null); return; }
    (async () => {
      const result = await fetchRoute(start, end);
      if (result) { setRoute(result.geometry); onRouteUpdate(result); }
    })();
  }, [start, end, map, onRouteUpdate]);
  return route.length > 0 ? <Polyline positions={route} color="#374151" weight={3} opacity={0.7} /> : null;
}

function ModalLocationPicker({ updateModal, setUpdateModal, isInProgress }) {
  const [clickCount, setClickCount] = useState(0);
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      const latF = lat.toFixed(6);
      const lngF = lng.toFixed(6);
      const coordinateText = `${latF}, ${lngF}`;
      if (isInProgress) {
        setUpdateModal(prev => ({ ...prev, newEndLat: latF, newEndLng: lngF, endSearch: coordinateText }));
      } else {
        if (clickCount % 2 === 0) {
          setUpdateModal(prev => ({ ...prev, newStartLat: latF, newStartLng: lngF, startSearch: coordinateText, mapCenter: [parseFloat(latF), parseFloat(lngF)] }));
        } else {
          setUpdateModal(prev => ({ ...prev, newEndLat: latF, newEndLng: lngF, endSearch: coordinateText }));
        }
        setClickCount(prev => prev + 1);
      }
    },
  });
  return null;
}

const showEmployeeStyleToast = (type, bId) => {
  const message = type === "accepted"
    ? `Driver accepted hire #${bId}. You can see details on my bookings.`
    : `Driver declined hire #${bId}. Try another one.`;
  toast(
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex-1">
        <strong className="text-sm">{type === "accepted" ? "Booking Accepted!" : "Booking Declined"}</strong>
        <p className="text-xs text-gray-600">{message}</p>
      </div>
      <button onClick={() => window.location.reload()} className={`px-2 py-1 text-xs rounded ${type === "accepted" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} text-white`}>Refresh</button>
    </div>,
    { autoClose: false, closeButton: true, position: "top-center" }
  );
};

const EmployeeMyBookings = () => {
  const dispatch = useDispatch();
  const { authKey, userDetails } = useSelector((state) => state.auth);
  const getBookings = async () => dispatch(fetchBookings());
  const getUsers = async () => dispatch(fetchUsers());
  const getVehicles = async () => dispatch(fetchVehicles());
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [scheduledBookings, setScheduledBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [drivers, setDrivers] = useState({});
  const [activeFilter, setActiveFilter] = useState(searchParams.get("filter") || "all");
  const [bookingType, setBookingType] = useState("realtime");
  const [cancellingBookings, setCancellingBookings] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const [updateModal, setUpdateModal] = useState({
    isOpen: false, booking: null, newStartLat: "", newStartLng: "", newEndLat: "", newEndLng: "",
    distance: null, mapCenter: DEFAULT_MAP_CENTER, startSearch: "", endSearch: "",
    startSuggestions: [], endSuggestions: [], routeInfo: null,
  });

  const isScheduledBookingStatus = (value) => {
    const status = String(value || "").trim().toUpperCase();
    return status === "S" || status === "SHEDULE BOOKING" || status === "SCHEDULE BOOKING";
  };

  const isRealtimeBookingStatus = (value) => {
    const status = String(value || "").trim().toUpperCase();
    return status === "R" || status === "REAL-TIME BOOKING" || status === "REAL TIME BOOKING";
  };

  useEffect(() => {
    const loadMainBookingsByType = async () => {
      if (!authKey || !userDetails?.ServiceNo) return;
      if (bookingType === "scheduled") setLoadingScheduled(true);
      else setLoading(true);
      try {
        const data = await getMainForkBookingDetails();
        if (Array.isArray(data)) {
          const myData = data.filter(b => String(b.bookingSno || "").trim() === String(userDetails.ServiceNo).trim());
          const scheduled = myData.filter(b => isScheduledBookingStatus(b.BookingStatus));
          const realtime = myData.filter(b => isRealtimeBookingStatus(b.BookingStatus));
          const sortByIdDesc = (a, b) => parseInt(b.bId || 0, 10) - parseInt(a.bId || 0, 10);
          setBookings(realtime.sort(sortByIdDesc));
          setScheduledBookings(scheduled.sort(sortByIdDesc));
        } else { setBookings([]); setScheduledBookings([]); }
        setCurrentPage(1);
      } catch (err) { console.error("Failed to load main bookings:", err); toast.error("Failed to load bookings"); }
      finally { setLoading(false); setLoadingScheduled(false); }
    };
    loadMainBookingsByType();
  }, [authKey, userDetails?.ServiceNo, bookingType]);

  useEffect(() => {
    const filterParam = searchParams.get("filter");
    setActiveFilter(filterParam || "all");
    setCurrentPage(1);
  }, [searchParams]);

  const sourceBookings = bookingType === "scheduled" ? scheduledBookings : bookings;
  const filteredBookings = sourceBookings.filter((booking) => {
    if (activeFilter === "all") return true;
    const status = (booking.status || "").toString().trim().toUpperCase();
    if (activeFilter === "pending") return status === "P" || status === "PENDING";
    if (activeFilter === "active") return status === "A" || status === "ACTIVE";
    if (activeFilter === "completed") return status === "CO" || status === "COMPLETED";
    if (activeFilter === "cancelled") return status === "C" || status === "CANCELLED" || status === "CANCELED";
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBookings = filteredBookings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  useEffect(() => {
    if (!authKey || !userDetails?.ServiceNo) return;
    if (!bookingWS.isConnected) bookingWS.connectAsEmployee(authKey);
    const handleStatusUpdate = (message) => {
      if (message.bookingSno && userDetails?.ServiceNo && message.bookingSno.toString() !== userDetails.ServiceNo.toString()) return;
      if (message.bId && message.status) {
        const normalizedStatus = normalizeStatus(message.status);
        const isAdminCancelled = normalizedStatus === "cancelled" && (message.cancelledBy === "admin" || message.type === "booking_cancelled");
        if (isAdminCancelled) toast.error(message.message || `Booking #${message.bId} cancelled by Admin`, { autoClose: 5000 });
        setBookings(prev => prev.map(b => b.bId?.toString() === message.bId?.toString() ? { ...b, status: normalizedStatus, dlatitude: message.dlatitude !== undefined ? message.dlatitude : b.dlatitude, dlongitude: message.dlongitude !== undefined ? message.dlongitude : b.dlongitude } : b));
        setScheduledBookings(prev => prev.map(b => b.bId?.toString() === message.bId?.toString() ? { ...b, status: normalizedStatus, dlatitude: message.dlatitude !== undefined ? message.dlatitude : b.dlatitude, dlongitude: message.dlongitude !== undefined ? message.dlongitude : b.dlongitude } : b));
        if (!isAdminCancelled) showStatusChangeNotification("", normalizedStatus);
      }
    };
    const unsubscribe = bookingWS.onStatusUpdate(handleStatusUpdate);
    return () => { if (unsubscribe) unsubscribe(); };
  }, [authKey, userDetails]);

  const normalizeStatus = (status) => {
    if (!status) return "pending";
    const s = status.toString().trim().toLowerCase();
    if (s === "p" || s === "pending") return "pending";
    if (s === "a" || s === "active") return "active";
    if (s === "en" || s === "enroute") return "enroute";
    if (s === "ip" || s === "inprogress" || s === "in progress") return "inprogress";
    if (s === "co" || s === "completed") return "completed";
    if (s === "c" || s === "canceled" || s === "cancelled") return "cancelled";
    return s;
  };

  const showStatusChangeNotification = (oldStatus, newStatus) => {
    const messages = { pending: "🟡 Booking is waiting for driver acceptance", active: "✅ Driver has accepted your booking", enroute: "🚗 Driver is on the way to pickup", inprogress: "⏱️ Trip is in progress", completed: "🎉 Trip completed successfully", cancelled: "❌ Booking has been cancelled" };
    if (normalizeStatus(oldStatus) !== newStatus) toast.info(messages[newStatus] || `Status updated to ${newStatus}`, { autoClose: 5000 });
  };

  useEffect(() => {
    const fetchDriverInfo = async () => {
      try {
        const userList = await getUsers();
        const driverMap = {};
        userList.forEach((user) => { driverMap[user.ServiceNo] = { name: user.Name, mobile: user.MobileNo }; });
        setDrivers(driverMap);
      } catch (err) { console.error("Failed to fetch driver info:", err); }
    };
    fetchDriverInfo();
  }, [authKey]);

  const handleCancel = async (bId) => {
    if (cancellingBookings.has(bId)) return;
    if (!window.confirm("Cancel this booking?")) return;
    setCancellingBookings(prev => new Set(prev).add(bId));
    try {
      const bookingToCancel = bookings.find(b => b.bId === bId);
      if (!bookingToCancel) { toast.error("Booking not found"); return; }
      const payload = [{ ...bookingToCancel, status: "C" }];
      await api.post("/BookingRegister/PostForkBooking", payload);
      setTimeout(() => {
        if (bookingWS.isReady()) bookingWS.sendCancellation({ bId: parseInt(bId), dSNo: bookingToCancel.dSNo, vId: bookingToCancel.vId, type: "booking_cancelled", bookingSno: userDetails.ServiceNo });
      }, 100);
      if (bookingToCancel.vId) {
        try {
          const allVehicles = await getVehicles();
          const vehicle = allVehicles?.find(v => v.vehicleId?.toString() === bookingToCancel.vId?.toString());
          if (vehicle) {
            const typeCodeMap = { Car: "C", Van: "V", Lorry: "L", SUV: "S", Cab: "CA", Bus: "B", "Cherry Picker": "CH", Forklift: "F", Crane: "CR", Trailer: "T" };
            const vehiclePayload = [{ vehicleId: vehicle.vehicleId, vehicleType: typeCodeMap[vehicle.vehicleType] || vehicle.vehicleType, weightCat: vehicle.weightCat || "", driverSno: vehicle.driverSno || "", status: vehicle.status === "Inactive" ? "I" : "A", bStatus: "", oStatus: vehicle.oStatus === "Online" ? "O" : "F", latitude: vehicle.latitude || "", longitude: vehicle.longitude || "" }];
            await api.post("/VehicleRegister/PostVehicleDetails", vehiclePayload);
            setTimeout(() => { locationWS.updateDriverData({ vehicleId: bookingToCancel.vId, vehicle_type: vehicle.vehicleType, bStatus: "", oStatus: vehicle.oStatus === "Online" ? "O" : "F" }); if (locationWS.isReady?.()) locationWS.sendLocationUpdate(); }, 200);
          }
        } catch (vehicleErr) { console.error("Failed to clear vehicle bStatus:", vehicleErr); }
      }
      setBookings(prev => prev.map(b => b.bId === bId ? { ...b, status: "C" } : b));
      toast.success("Booking cancelled successfully");
    } catch (err) { console.error("Cancel failed:", err); toast.error("Cancel failed"); }
    finally { setCancellingBookings(prev => { const newSet = new Set(prev); newSet.delete(bId); return newSet; }); }
  };

  const handleTrack = async (b) => {
    try {
      const bookingsData = await getBookings();
      const booking = bookingsData.find(bk => bk.bId?.toString() === b.bId?.toString());
      if (!booking) { toast.error("Booking not found"); return; }
      const status = (booking.status || "").toString().trim().toUpperCase();
      if (["A", "ACTIVE", "EN", "ENROUTE", "IP", "INPROGRESS"].includes(status)) { window.open(`/track?bId=${b.bId}`, "_blank"); return; }
      if (["CO", "COMPLETED"].includes(status)) toast.info("This booking has already been completed");
      else if (["C", "CANCEL", "CANCELLED"].includes(status)) toast.warn("This booking has been cancelled");
      else if (["P", "PENDING"].includes(status)) toast.info("Booking is still pending driver acceptance");
      window.location.reload();
    } catch (error) { console.error("Error checking booking status:", error); toast.error("Failed to verify booking status"); }
  };

  const handleCall = (booking) => {
    const driverMobile = booking.driverMobile || drivers[booking.dSNo]?.mobile;
    if (!driverMobile || driverMobile === "N/A") { toast.warn("Driver phone number not available"); return; }
    window.location.href = `tel:${driverMobile}`;
  };

  const getStatusInfo = (status) => {
    const s = normalizeStatus(status);
    if (s === "pending") return { label: "Pending", color: "bg-amber-100 text-amber-700" };
    if (s === "active") return { label: "Active", color: "bg-emerald-100 text-emerald-700" };
    if (s === "enroute") return { label: "En Route", color: "bg-blue-100 text-blue-700" };
    if (s === "inprogress") return { label: "In Progress", color: "bg-purple-100 text-purple-700" };
    if (s === "completed") return { label: "Completed", color: "bg-gray-100 text-gray-700" };
    if (s === "cancelled") return { label: "Cancelled", color: "bg-red-100 text-red-700" };
    return { label: status || "Unknown", color: "bg-gray-100 text-gray-600" };
  };

  const isPending = (status) => normalizeStatus(status) === "pending";
  const isActive = (status) => normalizeStatus(status) === "active";
  const isEnRoute = (status) => normalizeStatus(status) === "enroute";
  const isInProgress = (status) => normalizeStatus(status) === "inprogress";
  const isCancelled = (status) => normalizeStatus(status) === "cancelled";

  const openUpdateModal = (booking) => {
    setUpdateModal({ isOpen: true, booking, newStartLat: booking.slatitude, newStartLng: booking.slongitude, newEndLat: booking.elatitude, newEndLng: booking.elongitude, distance: parseFloat(booking.totalDestence) || null, mapCenter: [parseFloat(booking.slatitude), parseFloat(booking.slongitude)], startSearch: "", endSearch: "", startSuggestions: [], endSuggestions: [], routeInfo: null });
  };

  const closeUpdateModal = () => { setUpdateModal(prev => ({ ...prev, isOpen: false })); };

  const handleStartSearch = async (query) => {
    setUpdateModal(prev => ({ ...prev, startSearch: query }));
    if (query.trim().length > 2) { const results = await fetchORSResults(query); setUpdateModal(prev => ({ ...prev, startSuggestions: results })); }
    else setUpdateModal(prev => ({ ...prev, startSuggestions: [] }));
  };

  const handleEndSearch = async (query) => {
    setUpdateModal(prev => ({ ...prev, endSearch: query }));
    if (query.trim().length > 2) { const results = await fetchORSResults(query); setUpdateModal(prev => ({ ...prev, endSuggestions: results })); }
    else setUpdateModal(prev => ({ ...prev, endSuggestions: [] }));
  };

  const selectStartLocation = (feature) => {
    const [lng, lat] = feature.geometry.coordinates;
    setUpdateModal(prev => ({ ...prev, newStartLat: lat.toFixed(6), newStartLng: lng.toFixed(6), startSearch: feature.properties.label || "", startSuggestions: [], mapCenter: [lat, lng] }));
  };

  const selectEndLocation = (feature) => {
    const [lng, lat] = feature.geometry.coordinates;
    setUpdateModal(prev => ({ ...prev, newEndLat: lat.toFixed(6), newEndLng: lng.toFixed(6), endSearch: feature.properties.label || "", endSuggestions: [] }));
  };

  const clearMapLocations = () => {
    setUpdateModal(prev => ({ ...prev, newStartLat: "", newStartLng: "", newEndLat: "", newEndLng: "", startSearch: "", endSearch: "", startSuggestions: [], endSuggestions: [], routeInfo: null, mapCenter: prev.booking ? [parseFloat(prev.booking.slatitude), parseFloat(prev.booking.slongitude)] : DEFAULT_MAP_CENTER }));
  };

  const handleModalLocationUpdate = async () => {
    const { booking, newStartLat, newStartLng, newEndLat, newEndLng } = updateModal;
    if (!booking) return;
    const canChangeStart = isPending(booking.status) || isActive(booking.status) || isEnRoute(booking.status);
    if (!newEndLat || !newEndLng) { toast.error("Please select destination location"); return; }
    if (!canChangeStart && (!newStartLat || !newStartLng)) { toast.error("Cannot change pickup location when trip is in progress"); return; }
    try {
      const latestBookings = await getBookings();
      let latestBooking = latestBookings.find(item => item.bId?.toString() === booking.bId?.toString()) || booking;
      const getStatusCode = (status) => {
        const s = status?.toString().trim().toUpperCase() || "";
        if (s === "ACTIVE" || s === "A") return "A";
        if (s === "ENROUTE" || s === "EN") return "EN";
        if (s === "INPROGRESS" || s === "IP") return "IP";
        if (s === "COMPLETED" || s === "CO") return "CO";
        return s;
      };
      const updatePayload = [{ bId: latestBooking.bId, slatitude: canChangeStart ? newStartLat : latestBooking.slatitude, slongitude: canChangeStart ? newStartLng : latestBooking.slongitude, elatitude: newEndLat, elongitude: newEndLng, totalDestence: updateModal.routeInfo ? (updateModal.routeInfo.distance / 1000).toFixed(2) : latestBooking.totalDestence, status: getStatusCode(latestBooking.status), bookingSno: latestBooking.bookingSno, vId: latestBooking.vId, dSNo: latestBooking.dSNo, bookingDT: latestBooking.bookingDT, bsTime: latestBooking.bsTime, beTime: latestBooking.beTime, dlatitude: latestBooking.dlatitude, dlongitude: latestBooking.dlongitude }];
      const updateRes = await api.post("/BookingRegister/PostForkBooking", updatePayload);
      if (updateRes.data?.statusCode === 200) { toast.success("Locations updated successfully"); closeUpdateModal(); setTimeout(() => window.location.reload(), 1500); }
      else toast.error("Update failed: " + (updateRes.data?.result || "Unknown error"));
    } catch (err) { console.error("Update failed:", err); toast.error("Failed to update locations"); }
  };

  if (loading && bookingType === "realtime") return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-500">Loading real-time bookings...</div></div>;
  if (loadingScheduled && bookingType === "scheduled") return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-500">Loading scheduled bookings...</div></div>;

  return (
    <EmployeeLayout>
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">My Bookings</h1>

        {/* Booking Type Tabs */}
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-1 mb-4 flex gap-1">
          <button onClick={() => { setBookingType("realtime"); setCurrentPage(1); setActiveFilter("all"); }} className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${bookingType === "realtime" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"}`}>Real-Time Bookings</button>
          <button onClick={() => { setBookingType("scheduled"); setCurrentPage(1); setActiveFilter("all"); }} className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${bookingType === "scheduled" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"}`}>Scheduled Bookings</button>
        </div>

        {/* Filter Indicator */}
        {activeFilter !== "all" && (
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-3 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2"><span className="text-gray-500">Filter:</span><span className="text-sm font-medium text-gray-900 capitalize">{activeFilter}</span><span className="text-xs text-gray-400">({filteredBookings.length} bookings)</span></div>
            <button onClick={() => { setSearchParams({}); setActiveFilter("all"); }} className="text-xs text-gray-500 hover:text-gray-700">Clear Filter</button>
          </div>
        )}

        {filteredBookings.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-500 text-sm">No {activeFilter !== "all" ? activeFilter : ""} bookings found</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3">
              {currentBookings.map((b) => {
                const driverMobileNum = b.driverMobile || drivers[b.dSNo]?.mobile;
                const hasDriver = b.dSNo && driverMobileNum;
                const statusInfo = getStatusInfo(b.status);
                return (
                  <div key={b.bId} className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
                    <div className="bg-gray-50 border-b border-gray-100 p-3 flex items-center justify-between flex-wrap gap-2">
                      <div><span className="text-xs font-mono text-gray-500">#{b.bId}</span><p className="text-xs text-gray-400">{new Date(b.bookingDT).toLocaleDateString()} • {new Date(b.bookingDT).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p></div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>{statusInfo.label}</span>
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="flex items-start gap-2 text-xs"><div className="w-4 h-4 bg-emerald-500 rounded-full flex-shrink-0 mt-0.5"></div><div className="flex-1"><span className="text-gray-500">Pickup:</span><span className="text-gray-700 ml-1">{parseFloat(b.slatitude).toFixed(4)}, {parseFloat(b.slongitude).toFixed(4)}</span></div></div>
                      <div className="flex items-start gap-2 text-xs"><div className="w-4 h-4 bg-blue-500 rounded-full flex-shrink-0 mt-0.5"></div><div className="flex-1"><span className="text-gray-500">Drop:</span><span className="text-gray-700 ml-1">{parseFloat(b.elatitude).toFixed(4)}, {parseFloat(b.elongitude).toFixed(4)}</span></div></div>
                      {b.vId && <div className="pt-2 border-t border-gray-50"><p className="text-sm font-medium text-gray-800">{b.vId}</p><p className="text-xs text-gray-500">{b.driverName || b.dSNo || "Assigned"}</p></div>}
                    </div>
                    <div className="p-3 bg-gray-50 border-t border-gray-100">
                      {isPending(b.status) && <button onClick={() => handleCancel(b.bId)} disabled={cancellingBookings.has(b.bId)} className="w-full py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50">{cancellingBookings.has(b.bId) ? "Cancelling..." : "Cancel Booking"}</button>}
                      {(isActive(b.status) || isEnRoute(b.status)) && (
                        <div className="grid grid-cols-2 gap-2">
                          <button onClick={() => handleTrack(b)} className="py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition">Track</button>
                          <button onClick={() => handleCall(b)} disabled={!hasDriver} className={`py-2 rounded-lg text-sm font-medium transition ${hasDriver ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}>Call Driver</button>
                        </div>
                      )}
                      {isInProgress(b.status) && (
                        <div className="grid grid-cols-2 gap-2">
                          <button onClick={() => handleTrack(b)} className="py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition">Track</button>
                          <button onClick={() => handleCall(b)} disabled={!hasDriver} className={`py-2 rounded-lg text-sm font-medium transition ${hasDriver ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}>Call Driver</button>
                        </div>
                      )}
                      {isCancelled(b.status) && <div className="text-center py-2"><p className="text-xs text-gray-500">Booking cancelled</p></div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4 flex-wrap">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50">Previous</button>
                <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50">Next</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Update Modal */}
      {updateModal.isOpen && updateModal.booking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gray-50 border-b border-gray-100 p-4 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-base font-semibold text-gray-900">Update Booking Locations</h2>
              <button onClick={closeUpdateModal} className="p-1 hover:bg-gray-100 rounded transition"><span className="text-gray-500">✕</span></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-600">Booking ID: <span className="font-mono font-medium">#{updateModal.booking.bId}</span> • Status: <span className={getStatusInfo(updateModal.booking.status).color.split(' ')[0] + " px-2 py-0.5 rounded-full text-xs"}>{getStatusInfo(updateModal.booking.status).label}</span></p></div>

              {!isInProgress(updateModal.booking.status) && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Pickup Location</label>
                  <input type="text" placeholder="Search pickup location..." value={updateModal.startSearch} onChange={(e) => handleStartSearch(e.target.value)} className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400" />
                  {updateModal.startSuggestions.length > 0 && <ul className="border border-gray-200 rounded-lg mt-1 max-h-32 overflow-y-auto">{updateModal.startSuggestions.map((f, i) => <li key={i} onClick={() => selectStartLocation(f)} className="px-3 py-1.5 text-xs hover:bg-gray-50 cursor-pointer">{f.properties.label}</li>)}</ul>}
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Destination Location</label>
                <input type="text" placeholder="Search destination..." value={updateModal.endSearch} onChange={(e) => handleEndSearch(e.target.value)} className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400" />
                {updateModal.endSuggestions.length > 0 && <ul className="border border-gray-200 rounded-lg mt-1 max-h-32 overflow-y-auto">{updateModal.endSuggestions.map((f, i) => <li key={i} onClick={() => selectEndLocation(f)} className="px-3 py-1.5 text-xs hover:bg-gray-50 cursor-pointer">{f.properties.label}</li>)}</ul>}
              </div>

              <div className="h-[300px] rounded-lg overflow-hidden border border-gray-200">
                <MapContainer
                  center={DEFAULT_CENTER}
                  zoom={17} style={{ height: "100%", width: "100%" }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <ModalLocationPicker updateModal={updateModal} setUpdateModal={setUpdateModal} isInProgress={isInProgress(updateModal.booking.status)} />
                  {updateModal.newStartLat && updateModal.newStartLng && <Marker position={[parseFloat(updateModal.newStartLat), parseFloat(updateModal.newStartLng)]} icon={startIcon}><Popup>Pickup Location</Popup></Marker>}
                  {updateModal.newEndLat && updateModal.newEndLng && <Marker position={[parseFloat(updateModal.newEndLat), parseFloat(updateModal.newEndLng)]} icon={endIcon}><Popup>Destination</Popup></Marker>}
                  {updateModal.newStartLat && updateModal.newStartLng && updateModal.newEndLat && updateModal.newEndLng && <ModalRouteLayer start={[parseFloat(updateModal.newStartLat), parseFloat(updateModal.newStartLng)]} end={[parseFloat(updateModal.newEndLat), parseFloat(updateModal.newEndLng)]} onRouteUpdate={(routeInfo) => setUpdateModal(prev => ({ ...prev, routeInfo }))} />}
                </MapContainer>
              </div>

              {(updateModal.newStartLat || updateModal.newEndLat) && <button onClick={clearMapLocations} className="text-xs text-red-600 hover:text-red-700">Clear All Locations</button>}
              {updateModal.routeInfo && <div className="bg-gray-50 rounded-lg p-2 text-center"><span className="text-xs text-gray-600">Distance: {(updateModal.routeInfo.distance / 1000).toFixed(2)} km</span></div>}
              <div className="bg-blue-50 rounded-lg p-3"><p className="text-xs text-blue-700">{!isInProgress(updateModal.booking.status) ? "You can update both pickup and destination locations." : "Pickup location is locked. Only destination can be updated."}</p></div>
            </div>
            <div className="bg-gray-50 border-t border-gray-100 p-4 flex justify-end gap-2">
              <button onClick={closeUpdateModal} className="px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">Cancel</button>
              <button onClick={handleModalLocationUpdate} disabled={!updateModal.newEndLat || !updateModal.newEndLng} className="px-4 py-1.5 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50">Update Locations</button>
            </div>
          </div>
        </div>
      )}
    </EmployeeLayout>
  );
};

export default EmployeeMyBookings;
